import type { AppName, UserPermissions } from '../types';
import { PermissionRegistry } from './PermissionRegistry';

type ApiPermissions = Record<string, unknown>;

interface ReduxStoreLike {
  getState: () => {
    account?: {
      model?: {
        permissionsRaw?: Record<string, unknown>;
      };
    };
  };
}

const lunaResourceToPages: Record<string, string[]> = {
  face: ['faces'],
  list: ['faces'],
  event: ['events'],
  verifier: ['verifiers'],
  source: ['sources'],
  handler: ['handlers'],
  task: ['tasks'],
  account: ['accounts'],
  resources: ['checks'],
  emit_events: ['checks'],
};

function normalizePermissions(
  raw: Record<string, unknown>,
  resourceToPages: Record<string, string[]>,
): UserPermissions {
  const normalized: Record<string, string[]> = {};
  Object.entries(raw).forEach(([res, actions]) => {
    if (res === 'pages') return;
    if (Array.isArray(actions)) {
      normalized[res] = actions.map((a) => String(a).toLowerCase());
    } else if (typeof actions === 'object' && actions !== null && 'allowed' in actions) {
      normalized[res] = [actions.allowed ? 'allowed' : ''].filter(Boolean);
    }
  });

  const pages = [...new Set(
    Object.entries(normalized).flatMap(([res]) => resourceToPages[res] ?? []),
  )];

  return { ...normalized, pages };
}

function adaptArtemyPermissions(api: ApiPermissions | null | undefined): UserPermissions {
  const registry = PermissionRegistry.getInstance('point');
  const result: UserPermissions = {};

  if (!api || typeof api !== 'object') return result;

  Object.entries(api).forEach(([apiResource, actions]) => {
    if (apiResource === 'pages' || apiResource === 'ignore_visibility') return;
    if (!Array.isArray(actions)) return;

    const internalResource = registry.getInternalName(apiResource) ?? apiResource;
    result[internalResource] = actions.map((a) => String(a).toLowerCase());
  });

  if (Array.isArray(api.pages)) {
    result.pages = api.pages;
  }
  if (api.ignore_visibility) {
    result.ignore_visibility = true;
  }

  return result;
}

function adaptLunaPermissions(api: ApiPermissions | null | undefined): UserPermissions {
  const result: UserPermissions = {};

  if (!api || typeof api !== 'object') return result;

  Object.entries(api).forEach(([apiResource, actions]) => {
    if (apiResource === 'pages' || apiResource === 'ignore_visibility') return;
    if (!Array.isArray(actions)) return;

    const internalResource = String(apiResource).toLowerCase();
    result[internalResource] = actions.map((a) => String(a).toLowerCase());
  });

  if (Array.isArray(api.pages)) {
    result.pages = api.pages;
  }
  if (api.ignore_visibility) {
    result.ignore_visibility = true;
  }

  return result;
}

export function createPermissionFetcher(appName: AppName): () => Promise<UserPermissions> {
  switch (appName) {
    case 'point':
      return async () => {
        const permissions: Record<string, string[]> = {};
        return adaptArtemyPermissions(permissions);
      };
    case 'luna':
    case 'clementine':
      return async () => {
        return adaptLunaPermissions({});
      };
    default:
      return async () => ({});
  }
}

export function createPointPermissionFetcher(
  getSelf: () => Promise<{ role_id: string }>,
  getRole: (roleId: string) => Promise<{ permissions?: Record<string, string[]> }>,
): () => Promise<UserPermissions> {
  return async () => {
    const self = await getSelf();
    const role = await getRole(self.role_id);
    const permissions = adaptArtemyPermissions(role?.permissions ?? {});
    // ponytail: temporary hardcoded page for accounts until clementine accounts are properly handled
    permissions.pages = [...(permissions.pages ?? []), 'clementine_accounts'];
    return permissions;
  };
}

export function createLunaPermissionFetcher(
  getToken: (tokenId: string) => Promise<{ permissions?: Record<string, string[]> }>,
): (tokenId: string) => Promise<UserPermissions> {
  return async (tokenId: string) => {
    const token = await getToken(tokenId);
    return adaptLunaPermissions(token?.permissions);
  };
}

export function createLunaStorePermissionFetcher(
  store: ReduxStoreLike,
): () => Promise<UserPermissions> {
  return async () => {
    const state = store.getState();
    const rawPermissions = state.account?.model?.permissionsRaw ?? {};

    return normalizePermissions(rawPermissions, lunaResourceToPages);
  };
}

const clementineResourceToPages: Record<string, string[]> = {
  face: ['faces'],
  list: ['faces'],
  event: ['events'],
  verifier: ['verifiers'],
  source: ['sources'],
  handler: ['handlers'],
  task: ['tasks'],
  account: ['accounts'],
  resources: ['checks'],
  emit_events: ['checks'],
  department: ['departments'],
  notification: ['notifications'],
  thermo: ['thermo'],
  videoanalytics: ['videoAnalytics'],
  appInfo: ['appInfo'],
};

export function createClementinePermissionFetcher(
  store: { getState: () => { account?: { model?: { role?: number; permissionsRaw?: Record<string, unknown> } } } },
): () => Promise<UserPermissions> {
  return async () => {
    const state = store.getState();
    const rawPermissions = state.account?.model?.permissionsRaw;

    if (!rawPermissions || typeof rawPermissions !== 'object') {
      return { pages: [] };
    }

    return normalizePermissions(rawPermissions, clementineResourceToPages);
  };
}

export { adaptArtemyPermissions, adaptLunaPermissions };
